import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useAuthStore } from '@/entities/user';
import { WITHDRAWN_PATH } from '@/shared/config';
import { getErrorMessage } from '@/shared/lib';
import { withdrawAccountRequest } from './withdraw-account-request';

interface UseWithdrawAccountResult {
  readonly withdraw: () => void;
  readonly isPending: boolean;
  readonly errorMessage: string | null;
}

/**
 * 회원 탈퇴 실행 — 성공하면 useSignOut 과 같이 클라이언트 세션(auth store)과 쿼리 캐시를 비우고 완료 화면으로 보낸다.
 * 실패하면 세션을 건드리지 않고 메시지만 돌려줘 화면에 머문다.
 */
export const useWithdrawAccount = (): UseWithdrawAccountResult => {
  const navigate = useNavigate();
  const clear = useAuthStore((state) => state.clear);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: withdrawAccountRequest,
    onSuccess: () => {
      // 이동을 먼저 건다 — 세션을 비우면 보호 라우트 가드가 로그인으로 보내려 하므로 완료 화면 이동이 뒤에 와야 이긴다.
      void navigate(WITHDRAWN_PATH, { replace: true });
      clear();
      queryClient.clear();
    },
  });

  return {
    withdraw: () => mutation.mutate(),
    isPending: mutation.isPending,
    errorMessage: mutation.isError
      ? getErrorMessage(mutation.error, '탈퇴하지 못했습니다. 잠시 후 다시 시도해 주세요.')
      : null,
  };
};
